import type { BoundingSphere, HierarcicalObjectReference, ObjectData, ObjectId } from "@novorender/data-js-api";
import type { ReadonlyVec3 } from "gl-matrix";

/**
 * Engine-agnostic object data access.
 *
 * Components ask this for object metadata and hierarchy instead of reaching for
 * `db` (or `view.data`, or a BIM index) directly, so they neither know nor care
 * which backend answers, or whether the answer came from the network. One
 * implementation is chosen at startup and published on explorer globals — see
 * `ObjectDbDataAccess`, `BimIndexOfflineDataAccess` and `BimIndexDataAccess`.
 *
 * Deliberately narrow: every method here is one an existing consumer needs, and
 * consumers move over to it one at a time.
 *
 * List-returning methods yield `HierarcicalObjectReference`s: id, path and node
 * type — what a list or tree draws — plus `loadMetaData()` for the one row a user
 * actually opens. That keeps listing cheap (properties for every row are not free
 * on either backend) and it is what `db.search` already yields, so object-DB
 * results pass through untouched. Richer items are still in-type: `ObjectData`
 * extends `HierarcicalObjectReference`. A caller that really does read every row
 * asks for {@link FindOptions.full} instead of paying that read per row.
 *
 * **Aborting is answered, not thrown.** A method given a signal that fires resolves
 * with its own empty value — `undefined`, `[]`, an empty `Uint32Array` — and an
 * iterator simply stops yielding; nothing here rejects with an `AbortError`. So an
 * aborted call is indistinguishable from a genuinely empty answer unless the caller
 * re-checks its own signal, which a caller that acts on the result (painting, hiding,
 * "no results") must do.
 */
export interface ObjectDataAccess {
    /**
     * Which backend this is. Diagnostics only — never branch on it.
     *
     * A BIM index says which *kind* of BIM index it is, since a downloaded one and a
     * served one differ in what they need to answer rather than in what they answer.
     */
    readonly kind: "object-db" | "bim-index-offline" | "bim-index";

    /**
     * Whether the queries that are *not* a lookup by id — property queries, name
     * search, hierarchy — can be answered with no network.
     *
     * `false` on the object DB does not mean it has no index. It answers all of those
     * perfectly well; it just asks a server to. What the `false` says is that a caller
     * which has to keep working offline cannot rely on them, and should either stay on
     * id-based access ({@link getObject}, gated on {@link isDataAvailableOffline}) or
     * tell the user the feature needs a connection.
     *
     * This and {@link isDataAvailableOffline} are the only two methods here a consumer
     * is *meant* to branch on — {@link kind} is for diagnostics and must not be.
     */
    isIndexAvailableOffline(): boolean;

    /**
     * Whether objects can be fetched by id with no network — which in practice asks
     * whether the scene carries its own metadata.
     *
     * True on either backend for a scene that does, and false on either for one that
     * does not: the BIM index holds no per-object properties, so both backends read
     * objects from the very same place. An offline BIM index therefore still needs this
     * checked before {@link getObject} is trusted offline.
     */
    isDataAvailableOffline(): boolean;

    /** Full metadata for one object, or `undefined` when it cannot be resolved. */
    getObject(id: ObjectId, signal?: AbortSignal): Promise<ObjectData | undefined>;

    /**
     * The first object at `path`, or `undefined` when there is none — how a caller
     * reaches a node it only knows by path. "First" because a path is not unique in
     * every model, the same rule the path-keyed model tree follows.
     *
     * If you already have an object and want to get its parent, use getParent method instead.
     */
    getObjectAtPath(path: string, signal?: AbortSignal): Promise<ObjectData | undefined>;

    /**
     * The object's immediate parent, or `undefined` for a root.
     *
     * Takes the object rather than an id so each backend can use whichever identity
     * it indexes by — the path it already carries, or the id — without a lookup to
     * get there. A caller should not derive a parent path itself: which of the two
     * is cheaper is exactly the engine detail this interface hides.
     */
    getParent(object: HierarcicalObjectReference, signal?: AbortSignal): Promise<ObjectData | undefined>;

    /**
     * An object's children, or the root nodes when `parent` is `undefined`.
     *
     * Paging is the implementation's business: the iterator fetches more as it is
     * consumed and a caller that stops early (`break`) stops the fetching, so no
     * cursor crosses this boundary. Keeping a partially-consumed iterator around is
     * how a lazily-expanding tree resumes — the pattern `features/modelTree` already
     * uses with `db.search`.
     */
    getChildren(
        parent: HierarcicalObjectReference | undefined,
        signal?: AbortSignal,
    ): AsyncIterableIterator<HierarcicalObjectReference>;

    /**
     * Objects matching every term of {@link PropertyQuery} — "these objects, by
     * properties whose values I know".
     *
     * Exact matching, apart from the `contains` term — which is a real substring match
     * on both backends but the one part of the algebra that can answer short, so read
     * its caveat on {@link PropertyQuery} before composing with it.
     *
     * Rows, so a caller can read a path or open metadata. A caller that only wants the
     * ids — and especially one that wants the matches' subtrees too — should ask
     * {@link findIdsByProperties} instead, which is the same question in a form the
     * backends can answer without materializing a row per match.
     */
    findByProperties(query: PropertyQuery, options?: FindOptions): AsyncIterableIterator<HierarcicalObjectReference>;

    /**
     * Ids of the objects matching {@link PropertyQuery}, and with
     * `includeDescendants` everything beneath the matches that are not leaves —
     * "these objects and the subtrees they stand for".
     *
     * The same question {@link findByProperties} answers, reduced to what a caller that
     * paints, hides or selects actually uses. It is a method of its own rather than a
     * loop over the iterator because the reduction is exactly what the backends can
     * optimize: an index holds the match set as a bitmap and a subtree as a bitmap, so
     * the union is one operation on data it already has, where a caller stitching it
     * together spends a row (and, with descendants, a request) per match.
     *
     * **Distinct**, which the loop it replaces was not: a backend that unions bitmaps
     * de-duplicates for free while one that concatenates subtrees does not, and a caller
     * showing "hid N objects" must not get a different N per backend. Order is
     * unspecified. Packed for {@link getDescendantIds}' reason — with
     * `includeDescendants` this is the same order of magnitude, since one broad criterion
     * can name a subtree that is most of the model.
     *
     * A subtree a backend cannot read is left out rather than failing the whole answer,
     * for {@link getDescendantIds}' reason inverted: one unreadable container out of
     * thousands should cost its own ids, not everyone's.
     */
    findIdsByProperties(query: PropertyQuery, options?: FindIdsOptions): Promise<Uint32Array>;

    /**
     * Procore instance ids (`Procore/InstanceId`) for these objects, **one per input and in
     * the same order** — `null` for an id this scene version carries no object for, or whose
     * object has no instance id.
     *
     * The same mapping a `Procore/InstanceId` {@link findByProperties} would yield, and a
     * method of its own because every backend holds that mapping *as* a mapping: an id
     * translation endpoint on a served index, `id_mappings` on a downloaded one, `/resolve`
     * on the object DB. So this costs no row per object — but it also carries nothing else,
     * which is why a caller that wants the bounds or any other property still queries.
     *
     * Positional, and strictly so: repeats are answered once each, so the result and the
     * request always have the same length and zip directly. That is what makes it usable
     * for a whole selection, and also why a mapping the backend could only partly produce
     * answers `undefined` rather than something short.
     *
     * **`undefined` says the mapping is not available**, not that the objects have no
     * instance ids — a scene whose backend has no such endpoint, an index built without its
     * id mappings, or an aborted call. It means "ask the query instead", so a caller that
     * needs the mapping either way has to carry that fallback; see
     * `novolib/utils.collectAllObjectsByObjectId`.
     *
     * The whole list goes in one call. How it is split into requests, and which ids are
     * worth sending at all, is the implementation's business.
     */
    resolveObjectIdsToInstanceIds(objectIds: ObjectId[], signal?: AbortSignal): Promise<(string | null)[] | undefined>;

    /**
     * Object ids for these Procore instance ids, one per input and in the same order —
     * `null` for an instance id this scene version does not hold.
     *
     * {@link resolveObjectIdsToInstanceIds} inverted, with the same contract. Note that only
     * the current `Procore/InstanceId` generation is a mapping any backend holds: the legacy
     * `Procore/Id` is an ordinary property, and asking about one here answers `null`.
     */
    resolveInstanceIdsToObjectIds(
        instanceIds: string[],
        signal?: AbortSignal,
    ): Promise<(ObjectId | null)[] | undefined>;

    /**
     * The object's ancestor chain, root first, excluding the object itself.
     *
     * Ancestors a backend cannot produce are left out rather than guessed, so the
     * result may be shorter than the object's path is deep — a caller that stitches
     * a tree must cope with gaps.
     */
    getAncestors(object: HierarcicalObjectReference, signal?: AbortSignal): Promise<HierarcicalObjectReference[]>;

    /**
     * Ids of everything below the object, for the whole subtree, **excluding the
     * object itself** — a leaf therefore reports none.
     *
     * Ids rather than objects because callers use this to paint or hide, not to read
     * properties — and packed, because the set reaches hundreds of thousands on a
     * real model. Both backends produce one natively: the object DB's pre-computed
     * descendants file is packed on load, and the index materializes a bitmap.
     */
    getDescendantIds(object: HierarcicalObjectReference, signal?: AbortSignal): Promise<Uint32Array>;

    /**
     * Make the object's subtree ids available on it (`object.descendants`) *if that is
     * cheap*, and report whether they are now there.
     *
     * For background warm-ups: a caller doing this for hundreds of nodes must not
     * trigger hundreds of expensive subtree walks. The object DB uses its pre-computed
     * descendants file and gives up when there isn't one, rather than falling back to a
     * paginated search the way {@link getDescendantIds} does.
     *
     * "Cheap" is per backend, not universally free — a served index spends one subtree
     * request per object and a downloaded one an OPFS read, cheap only against the walk
     * each replaces. Both then keep the shrunk set for as long as their cache does. So a
     * caller bounds how many nodes one run covers rather than assuming the backend will.
     *
     * A `false` is not "no descendants" — an empty subtree warms and reports `true`. It
     * means the ids are not on the object (declined, aborted or failed), and
     * {@link getDescendantIds} is how to get them.
     */
    warmDescendantIds(object: HierarcicalObjectReference, signal?: AbortSignal): Promise<boolean>;

    /**
     * The volume enclosing a set of objects.
     */
    getBounds(ids: Iterable<ObjectId>, options?: BoundsOptions): Promise<SelectionBounds | undefined>;

    /**
     * What a click on `id` actually selects: the object standing for it, and every id to
     * highlight for it.
     *
     * A method rather than a caller's walk over {@link getAncestors} and
     * {@link getDescendantIds} because a BIM index answers it with one `climb`, where
     * stitching it together costs a metadata read per ancestor.
     *
     * `undefined` for an abort *and* for an id that cannot be resolved — not
     * distinguished, per the abort contract above.
     */
    resolveSelection(id: ObjectId, options: SelectionResolutionOptions): Promise<SelectionTarget | undefined>;

    /**
     * Let go of whatever the backend holds open, for a session that is replacing it.
     *
     * A no-op on the backends that hold nothing of their own — the object DB and the
     * served index both borrow what they use. `BimIndexOfflineDataAccess` closes the
     * index worker, which is what unlocks the OPFS files it reads and hands its WASM
     * memory back, and is why this exists: those files cannot be deleted while a
     * worker holds sync access handles on them.
     *
     * The instance is not to be queried afterwards, so whoever disposes one is
     * whoever replaces it (see `pickDataAccess`).
     */
    dispose(): void;
}

/**
 * Which object in the picked id's ancestor chain stands for it — Navisworks' names, and
 * the app's own setting. `last-object` is the deepest, `first-object` the shallowest.
 */
export type SelectionResolutionMode = "first-object" | "last-object";

/** What {@link ObjectDataAccess.resolveSelection} is asked for. */
export interface SelectionResolutionOptions {
    mode: SelectionResolutionMode;
    /**
     * Whether to resolve by `Procore/NodeClass` — the property saying whether a node is a
     * file, a container, a real object or a fragment of one.
     *
     * The caller's decision (ultimately a host feature flag), never an implementation's.
     * `false` behaves exactly as before the property existed, and so does a `true` click
     * on a node whose chain carries no class — so a federated scene mixing stamped and
     * unstamped files resolves each correctly.
     */
    classAware: boolean;
    signal?: AbortSignal;
}

/** What a click resolves to — see {@link ObjectDataAccess.resolveSelection}. */
export interface SelectionTarget {
    /** The object the click selects. */
    mainObjectId: ObjectId;
    /**
     * That object's properties, when resolving produced them anyway — a BIM index climbs
     * and reads in one request.
     *
     * Absent is not "no such object": the object DB resolves `last-object` without reading
     * anything, which is what keeps that click free. Ask {@link ObjectDataAccess.getObject}
     * when the properties are needed either way.
     */
    mainObject?: ObjectData;
    /**
     * The main object plus, when it is a grouping node, its whole subtree — so an object
     * rolled up from a fragment renders all of its geometry.
     *
     * Packed for {@link ObjectDataAccess.getDescendantIds}' reason: a click near the top
     * of a federated model can name most of it.
     */
    highlightIds: Uint32Array;
    /**
     * Whether a node class decided the answer (`resolved`) or the pre-`NodeClass` path walk
     * did (`legacy`). Diagnostics only — never branch on it. Returned rather than logged
     * so one caller emits one event for all three backends.
     */
    outcome: "resolved" | "legacy";
}

/** Which enclosing shape(s) {@link ObjectDataAccess.getBounds} should compute. */
export type BoundsParts = "box" | "sphere" | "both";

/**
 * The volume enclosing a set of objects. Only the part(s) that were asked for are
 * present; an absent one says nothing about whether the selection has that geometry.
 */
export interface SelectionBounds {
    box?: { min: ReadonlyVec3; max: ReadonlyVec3 };
    sphere?: BoundingSphere;
}

/** What {@link ObjectDataAccess.getBounds} is asked for. */
export interface BoundsOptions {
    /**
     * Defaults to `"sphere"` — every consumer in this app frames a camera, and a sphere
     * is what `renderActions.setCamera` takes. Asking for one part rather than both
     * roughly halves the work on an index backend, which probes each separately.
     */
    parts?: BoundsParts;
    signal?: AbortSignal;
}

/** What {@link ObjectDataAccess.findByProperties} is asked for. */
export interface FindOptions {
    /**
     * Have the rows arrive carrying their metadata, so `loadMetaData()` on every one of
     * them costs nothing further — how a caller reads a property (or the bounds) off
     * *each* match rather than off the one row a user opens.
     *
     * Off by default, because that is not free: properties for every row are a wider
     * response on the object DB and a wider page (`view=FULL`) on a served index. Ask for
     * it only when the loop really does read every match — and note that it buys nothing
     * for a caller that stops after a few rows.
     *
     * The rows are still `HierarcicalObjectReference`s and the metadata is still reached
     * through `loadMetaData()`: what changes is what that call costs, which is the one
     * thing a caller cannot see for itself. Reading `properties` off a row directly
     * happens to work on some backends and is not part of this contract.
     */
    full?: boolean;
    signal?: AbortSignal;
}

/**
 * What {@link ObjectDataAccess.findIdsByProperties} is asked for.
 *
 * The signal rides in here rather than as a second positional the way most other
 * methods take it, for {@link FindOptions}' reason: a method with an option of its own
 * reads better with one bag than with a third positional.
 */
export interface FindIdsOptions {
    /**
     * Also include everything beneath each match that is not a leaf.
     *
     * A container that matches stands for its whole subtree — which is what "similar
     * objects" means to someone hiding or isolating them. Off by default: the method
     * finds ids by properties, and the subtrees are the extra thing a caller opts into.
     */
    includeDescendants?: boolean;
    signal?: AbortSignal;
}

/**
 * What one {@link PropertyTerm} asks of its property — exactly one of these.
 *
 * The absent members are typed `never` rather than left out so that a term narrows
 * on a plain truthiness check, and so that a caller cannot pass two at once.
 */
type PropertyMatch =
    /** Holds any one of these values, compared exactly (case-insensitively). */
    | { anyOf: readonly string[] | readonly number[]; range?: never; defined?: never; contains?: never }
    /** Holds a value within these inclusive bounds. */
    | { anyOf?: never; range: { min: string; max: string }; defined?: never; contains?: never }
    /** Holds any value at all — the property is simply present. */
    | { anyOf?: never; range?: never; defined: true; contains?: never }
    /**
     * Holds a value containing this substring (case-insensitively). Read the caveat on
     * {@link PropertyQuery} before reaching for it.
     */
    | { anyOf?: never; range?: never; defined?: never; contains: string };

/** One condition on one property. */
export type PropertyTerm = PropertyMatch & {
    /** Property name, or one of `id` / `path` / `name`. */
    property: string;
    /** Match objects that *fail* the condition instead. */
    negate?: boolean;
};

/**
 * A conjunction of per-property conditions.
 *
 * This is deliberately the *intersection* of what the backends can express, not the
 * union: `SearchPattern[]` on the object DB and `AND` over
 * `IN`/`RANGE`/`DEFINED`/`CONTAINS` predicates on a BIM index, each a near-direct
 * translation of a term.
 *
 * Two absences are the design:
 *
 * - **No OR across properties.** The index has an `OR`, but `SearchPattern[]` is
 *   ANDed with no way to express one, so an `or` here would be a method only some
 *   implementations could honour. OR *within* one property is `anyOf`.
 * - **No full-text.** Matching tokens across an object's properties has no
 *   `SearchPattern` at all, and the index's accelerated `SEARCH` column does not look
 *   at the object name — so it could satisfy neither backend nor caller.
 *
 * Every term but one is answered exactly. `contains` is exact in *what* it asks — a
 * non-exact `SearchPattern` and the index's `CONTAINS` are the same substring question
 * — but the index answers it by scanning an unaccelerated column, so a broad term on a
 * large model can hit the query deadline and come back short. Served, that is warned
 * about; offline it is silent. So it is not a term to compose freely: keep it narrow (a
 * file extension, not a common word), and treat a term a user typed as the case most
 * likely to answer short.
 *
 * Subtree scoping is the widening to expect next — several call sites still on `db`
 * combine `parentPath` with a `searchPattern`. Both backends can push it down (the
 * object DB as `parentPath`, the index through the engine's `filteredSubtree`), so
 * it belongs here rather than in a caller that would otherwise pull a whole subtree
 * to filter it; it waits only on the worker call and a consumer to prove it.
 */
export interface PropertyQuery {
    /** ANDed together. An empty list matches nothing rather than everything. */
    terms: readonly PropertyTerm[];
}

/** The common case: one property, one set of accepted values. */
export function byProperty(property: string, values: readonly string[] | readonly number[]): PropertyQuery {
    return { terms: [{ property, anyOf: values }] };
}
